import { useState, useEffect, useCallback } from 'react';
import { ArrowDownLeft, ArrowUpRight, Plus, Send, Loader, Lock, Shield, ShieldCheck, Copy, Check, AlertCircle, Wallet, Trophy, Gift, Tag, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';
import { useWalletLock } from '../context/WalletLockContext';
import { useWalletData, callSubmitDeposit, callSubmitWithdrawal, usePaymentMethodConfig } from '../services/walletService';
import type { PaymentMethodInfo, PaymentMethodNumber } from '../services/walletService';
import { validateDepositAmount, validateWithdrawalAmount, validateTransactionId, validatePhoneRequired } from '../utils/validation';

import TCoinIcon from '../components/TCoinIcon';
import PinKeypad from '../components/PinKeypad';
import {
    collection, query, where, orderBy, limit,
    onSnapshot, doc, getDoc, type Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

const methods = ['bKash', 'Nagad', 'Rocket'];
const isMock = import.meta.env.VITE_MOCK_AUTH === 'true';

const mockPromos = [
    { code: 'LAUNCH50', rewardAmount: 50, validForGame: 'all', currentUses: 67, maxUses: 100, lifespanHours: 72, active: true, goLiveDate: new Date('2026-01-01'), usedBy: [] as string[], requireFullSquad: false },
    { code: 'SQUAD100', rewardAmount: 100, validForGame: 'pubg', currentUses: 12, maxUses: 50, lifespanHours: 48, active: true, goLiveDate: new Date('2026-01-01'), usedBy: [] as string[], requireFullSquad: true, squadGameRequirement: 'pubg' },
];

const promoGameLabels: Record<string, string> = {
    all: 'All Games', pubg: 'PUBG Mobile', freefire: 'Free Fire', mlbb: 'MLBB', wildrift: 'Wild Rift',
};

export default function WalletPage() {
    const { showAlert } = useModal();
    const { user } = useAuth();
    const { isWalletUnlocked, hasPinSetup, checking, setupPin, verifyPin, lockWallet } = useWalletLock();
    const { wallet, transactions, loading } = useWalletData(user?.uid);

    const [tab, setTab] = useState<'history' | 'deposit' | 'withdraw'>('history');
    const [depositMethod, setDepositMethod] = useState('');
    const [withdrawMethod, setWithdrawMethod] = useState(methods[0]);
    const [submitting, setSubmitting] = useState(false);
    const [copied, setCopied] = useState(false);
    const { data: paymentConfig, loading: paymentConfigLoading } = usePaymentMethodConfig();
    const [pinError, setPinError] = useState('');

    // ── PIN Setup step (confirm PIN) ──
    const [setupStep, setSetupStep] = useState<'enter' | 'confirm'>('enter');
    const [firstPin, setFirstPin] = useState('');

    // ── History Sub-Tabs ──
    const [activeHistoryTab, setActiveHistoryTab] = useState<'all' | 'deposits' | 'withdrawals' | 'tournaments'>('all');

    // Lazy fetch state
    const [depositsFetched, setDepositsFetched] = useState(false);
    const [withdrawalsFetched, setWithdrawalsFetched] = useState(false);
    const [tournamentsFetched, setTournamentsFetched] = useState(false);
    const [depositsLoading, setDepositsLoading] = useState(false);
    const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
    const [tournamentsLoading, setTournamentsLoading] = useState(false);

    // Cached data for sub-tabs
    const [depositRequests, setDepositRequests] = useState<any[]>([]);
    const [withdrawalRequests, setWithdrawalRequests] = useState<any[]>([]);
    const [tournamentTxns, setTournamentTxns] = useState<any[]>([]);

    // Copy ID state (for sub-tab rows)
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // ── Promo Code State ──
    const [showPromoInput, setShowPromoInput] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [promoPreview, setPromoPreview] = useState<{
        rewardAmount: number;
        validForGame: string;
        currentUses: number;
        maxUses: number;
        lifespanHours: number;
        codeId: string;
        requireFullSquad: boolean;
        squadGameRequirement?: string;
    } | null>(null);
    const [promoChecking, setPromoChecking] = useState(false);
    const [promoRedeeming, setPromoRedeeming] = useState(false);
    const [promoError, setPromoError] = useState<string | null>(null);
    const [promoSuccess, setPromoSuccess] = useState<string | null>(null);

    // ── Penalty / Frozen State ──
    const [frozenClickMsg, setFrozenClickMsg] = useState(false);
    const [withdrawAmountStr, setWithdrawAmountStr] = useState('');

    const handleCopyId = useCallback((id: string) => {
        navigator.clipboard.writeText(id).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 1500);
        });
    }, []);

    // ── Lazy fetch: Deposits tab (from requests collection) ──
    useEffect(() => {
        if (activeHistoryTab !== 'deposits' || depositsFetched || isMock || !user?.uid) return;

        setDepositsLoading(true);
        const q = query(
            collection(db, 'requests'),
            where('userId', '==', user.uid),
            where('type', '==', 'deposit'),
            orderBy('createdAt', 'desc'),
            limit(20),
        );
        const unsub = onSnapshot(q, (snap) => {
            const docs = snap.docs.map((d) => {
                const data = d.data();
                let createdAtStr = '';
                if (data.createdAt) {
                    const ts = data.createdAt as Timestamp;
                    createdAtStr = ts.toDate ? ts.toDate().toLocaleString() : String(data.createdAt);
                }
                return { id: d.id, ...data, createdAt: createdAtStr };
            });
            setDepositRequests(docs);
            setDepositsLoading(false);
            setDepositsFetched(true);
        }, (err) => {
            console.error('Deposits fetch error:', err);
            setDepositsLoading(false);
            setDepositsFetched(true);
        });

        return () => unsub();
    }, [activeHistoryTab, depositsFetched, user?.uid]);

    // ── Lazy fetch: Withdrawals tab (from requests collection) ──
    useEffect(() => {
        if (activeHistoryTab !== 'withdrawals' || withdrawalsFetched || isMock || !user?.uid) return;

        setWithdrawalsLoading(true);
        const q = query(
            collection(db, 'requests'),
            where('userId', '==', user.uid),
            where('type', '==', 'withdrawal'),
            orderBy('createdAt', 'desc'),
            limit(20),
        );
        const unsub = onSnapshot(q, (snap) => {
            const docs = snap.docs.map((d) => {
                const data = d.data();
                let createdAtStr = '';
                if (data.createdAt) {
                    const ts = data.createdAt as Timestamp;
                    createdAtStr = ts.toDate ? ts.toDate().toLocaleString() : String(data.createdAt);
                }
                return { id: d.id, ...data, createdAt: createdAtStr };
            });
            setWithdrawalRequests(docs);
            setWithdrawalsLoading(false);
            setWithdrawalsFetched(true);
        }, (err) => {
            console.error('Withdrawals fetch error:', err);
            setWithdrawalsLoading(false);
            setWithdrawalsFetched(true);
        });

        return () => unsub();
    }, [activeHistoryTab, withdrawalsFetched, user?.uid]);

    // ── Lazy fetch: Tournaments tab (from transactions collection) ──
    useEffect(() => {
        if (activeHistoryTab !== 'tournaments' || tournamentsFetched || isMock || !user?.uid) return;

        setTournamentsLoading(true);
        const q = query(
            collection(db, 'transactions'),
            where('userId', '==', user.uid),
            where('type', 'in', ['entry', 'reward']),
            orderBy('createdAt', 'desc'),
            limit(20),
        );
        const unsub = onSnapshot(q, (snap) => {
            const docs = snap.docs.map((d) => {
                const data = d.data();
                let createdAtStr = '';
                if (data.createdAt) {
                    const ts = data.createdAt as Timestamp;
                    createdAtStr = ts.toDate ? ts.toDate().toLocaleString() : String(data.createdAt);
                }
                return { id: d.id, ...data, createdAt: createdAtStr };
            });
            setTournamentTxns(docs);
            setTournamentsLoading(false);
            setTournamentsFetched(true);
        }, (err) => {
            console.error('Tournaments fetch error:', err);
            setTournamentsLoading(false);
            setTournamentsFetched(true);
        });

        return () => unsub();
    }, [activeHistoryTab, tournamentsFetched, user?.uid]);

    // ── Reset all fetched flags on unmount so data refreshes on next visit ──
    useEffect(() => {
        return () => {
            setDepositsFetched(false);
            setWithdrawalsFetched(false);
            setTournamentsFetched(false);
        };
    }, []);

    // Pre-calculate limits for the withdraw tab
    const todayStr = new Date().toISOString().slice(0, 10);
    const todayWithdrawals = transactions.filter(tx => {
        if (tx.type !== 'withdrawal') return false;
        let dateStr = '';
        if (typeof tx.createdAt === 'string') {
            dateStr = tx.createdAt;
        } else {
            const dateObj = tx.createdAt as any;
            if (dateObj?.toDate) {
                dateStr = dateObj.toDate().toISOString();
            } else if (dateObj?.seconds) {
                dateStr = new Date(dateObj.seconds * 1000).toISOString();
            }
        }
        return dateStr.startsWith(todayStr);
    }).length;

    const remainingWithdrawals = Math.max(0, 2 - todayWithdrawals);
    const withdrawalLimitReached = remainingWithdrawals === 0;

    // ── Promo Code Handlers ──
    const handlePromoCheck = async () => {
        const code = promoCode.trim().toUpperCase();
        if (!code) return;
        setPromoError(null);
        setPromoPreview(null);
        setPromoSuccess(null);
        setPromoChecking(true);

        if (isMock) {
            await new Promise(r => setTimeout(r, 600));
            const found = mockPromos.find(p => p.code === code);
            if (!found || !found.active) { setPromoError('Invalid or expired promo code.'); setPromoChecking(false); return; }
            if (found.currentUses >= found.maxUses) { setPromoError('This code has been fully claimed.'); setPromoChecking(false); return; }
            if (found.goLiveDate > new Date()) { setPromoError('This code unlocks soon! Check back later.'); setPromoChecking(false); return; }
            setPromoPreview({
                rewardAmount: found.rewardAmount,
                validForGame: found.validForGame,
                currentUses: found.currentUses,
                maxUses: found.maxUses,
                lifespanHours: found.lifespanHours,
                codeId: found.code,
                requireFullSquad: found.requireFullSquad,
                squadGameRequirement: (found as any).squadGameRequirement,
            });
            setPromoChecking(false);
            return;
        }

        // Live mode — client-side validation
        try {
            const snap = await getDoc(doc(db, 'promo_codes', code));
            if (!snap.exists() || snap.data().active === false) { setPromoError('Invalid or expired promo code.'); setPromoChecking(false); return; }
            const d = snap.data();
            if ((d.currentUses ?? 0) >= d.maxUses) { setPromoError('This code has been fully claimed.'); setPromoChecking(false); return; }
            const goLive = d.goLiveDate?.toDate ? d.goLiveDate.toDate() : new Date(d.goLiveDate);
            if (goLive > new Date()) { setPromoError('This code unlocks soon! Check back later.'); setPromoChecking(false); return; }
            if (Array.isArray(d.usedBy) && user?.uid && d.usedBy.includes(user.uid)) { setPromoError('You have already redeemed this code.'); setPromoChecking(false); return; }
            setPromoPreview({
                rewardAmount: d.rewardAmount,
                validForGame: d.validForGame,
                currentUses: d.currentUses ?? 0,
                maxUses: d.maxUses,
                lifespanHours: d.lifespanHours,
                codeId: snap.id,
                requireFullSquad: d.requireFullSquad ?? false,
                squadGameRequirement: d.squadGameRequirement ?? undefined,
            });
        } catch (err) {
            console.error('Promo check error:', err);
            setPromoError('Failed to check code. Please try again.');
        } finally {
            setPromoChecking(false);
        }
    };

    const handlePromoRedeem = async () => {
        if (!promoPreview) return;
        setPromoRedeeming(true);
        setPromoError(null);

        if (isMock) {
            await new Promise(r => setTimeout(r, 1000));
            const game = promoGameLabels[promoPreview.validForGame] || promoPreview.validForGame;
            let msg = `✅ ${promoPreview.rewardAmount} T Coin promo credits added to your wallet!\nValid for: ${game}. Expires in ${promoPreview.lifespanHours} hours.`;
            if (promoPreview.requireFullSquad) {
                msg += `\nYour squad entry fees will be covered first when joining a ${game} tournament.`;
            }
            setPromoSuccess(msg);
            setPromoPreview(null);
            setPromoCode('');
            setPromoRedeeming(false);
            return;
        }

        // Live mode
        try {
            const redeemFn = httpsCallable<{ code: string }, { success: boolean; message: string }>(functions, 'redeemPromoCode');
            await redeemFn({ code: promoCode.trim().toUpperCase() });
            const game = promoGameLabels[promoPreview.validForGame] || promoPreview.validForGame;
            let msg = `✅ ${promoPreview.rewardAmount} T Coin promo credits added to your wallet!\nValid for: ${game}. Expires in ${promoPreview.lifespanHours} hours.`;
            if (promoPreview.requireFullSquad) {
                msg += `\nYour squad entry fees will be covered first when joining a ${game} tournament.`;
            }
            setPromoSuccess(msg);
            setPromoPreview(null);
            setPromoCode('');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Failed to redeem code. Please try again.';
            setPromoError(msg);
        } finally {
            setPromoRedeeming(false);
        }
    };


    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'deposit': return <ArrowDownLeft size={16} style={{ color: 'var(--success)' }} />;
            case 'withdrawal': return <ArrowUpRight size={16} style={{ color: 'var(--danger)' }} />;
            case 'entry': return <Send size={16} style={{ color: 'var(--warning)' }} />;
            case 'reward': return <Plus size={16} style={{ color: 'var(--neon-blue)' }} />;
            default: return null;
        }
    };

    // ── Payment method sub-tab state ──
    const [activeNumberType, setActiveNumberType] = useState<'personal' | 'merchant' | 'agent' | null>(null);

    // Get the payment method info for the currently selected method
    const getMethodKey = (label: string): string => label.toLowerCase().replace(/\s/g, '');
    const currentMethodKey = getMethodKey(depositMethod);
    const currentMethodInfo = paymentConfig ? (paymentConfig as Record<string, PaymentMethodInfo | undefined>)[currentMethodKey] : undefined;

    // Derive active numbers and group by type
    const activeNumbers: PaymentMethodNumber[] = currentMethodInfo?.numbers?.filter(n => n.active) ?? [];
    const numberTypes = [...new Set(activeNumbers.map(n => n.type))] as ('personal' | 'merchant' | 'agent')[];
    const hasMultipleTypes = numberTypes.length > 1;
    const effectiveType = hasMultipleTypes ? activeNumberType : (numberTypes[0] ?? null);
    const displayedNumber = activeNumbers.find(n => n.type === effectiveType) ?? null;

    // Auto-select first type when method changes
    useEffect(() => {
        if (numberTypes.length > 0 && (!activeNumberType || !numberTypes.includes(activeNumberType))) {
            setActiveNumberType(numberTypes[0]);
        }
    }, [depositMethod, numberTypes.join()]);

    const typeLabels: Record<string, { label: string; instruction: string; color: string; bg: string; border: string }> = {
        personal: { label: 'Send Money', instruction: 'Send Money', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.25)' },
        merchant: { label: 'Payment', instruction: 'Payment', color: '#a855f7', bg: 'rgba(168,85,247,0.12)', border: 'rgba(168,85,247,0.25)' },
        agent:    { label: 'Cash Out', instruction: 'Cash Out', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.25)' },
    };

    const handleCopyNumber = (number: string) => {
        navigator.clipboard.writeText(number).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    const handleDeposit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        const amount = Number((form.elements.namedItem('amount') as HTMLInputElement).value);
        const transactionId = (form.elements.namedItem('transactionId') as HTMLInputElement).value;
        const senderNumber = (form.elements.namedItem('senderNumber') as HTMLInputElement).value;

        if (!depositMethod || !amount || !transactionId || !senderNumber) {
            showAlert('Please fill in all fields.', 'Missing Fields');
            return;
        }

        // Client-side validation (defense-in-depth)
        const amtCheck = validateDepositAmount(amount);
        if (!amtCheck.valid) { showAlert(amtCheck.message!, 'Invalid Amount'); return; }
        const txnCheck = validateTransactionId(transactionId);
        if (!txnCheck.valid) { showAlert(txnCheck.message!, 'Invalid Transaction ID'); return; }
        const phoneCheck = validatePhoneRequired(senderNumber);
        if (!phoneCheck.valid) { showAlert(phoneCheck.message!, 'Invalid Phone'); return; }

        setSubmitting(true);
        try {
            const result = await callSubmitDeposit({
                amount,
                method: depositMethod,
                transactionId,
                senderNumber,
            });
            showAlert(`✅ ${result.message}`, 'Deposit Submitted');
            form.reset();
            setDepositMethod('');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
            showAlert(`❌ ${msg}`, 'Deposit Failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleWithdraw = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        const amount = Number((form.elements.namedItem('amount') as HTMLInputElement).value);
        const method = (form.elements.namedItem('method') as HTMLSelectElement).value;
        const receiverNumber = (form.elements.namedItem('receiverNumber') as HTMLInputElement).value;
        const confirmReceiverNumber = (form.elements.namedItem('confirmReceiverNumber') as HTMLInputElement).value;

        if (!amount || !receiverNumber || !confirmReceiverNumber) {
            showAlert('Please fill in all fields.', 'Missing Fields');
            return;
        }

        if (receiverNumber !== confirmReceiverNumber) {
            showAlert('Receiver numbers do not match. Please verify carefully.', 'Mismatched Number');
            return;
        }

        // Client-side validation (defense-in-depth)
        const amtCheck = validateWithdrawalAmount(amount, wallet.balance);
        if (!amtCheck.valid) { showAlert(amtCheck.message!, 'Invalid Amount'); return; }
        const phoneCheck = validatePhoneRequired(receiverNumber);
        if (!phoneCheck.valid) { showAlert(phoneCheck.message!, 'Invalid Phone'); return; }

        setSubmitting(true);
        try {
            const result = await callSubmitWithdrawal({
                amount,
                method,
                receiverNumber,
            });
            const wdrId = result.withdrawalId;
            showAlert(
                `✅ ${result.message}\n\nYour Withdrawal ID: ${wdrId ?? 'N/A'}\n\nPlease save this ID — you will need it if you ever contact support about this withdrawal. Withdrawals are typically processed within 24 hours.`,
                'Withdrawal Submitted'
            );
            form.reset();
            setWithdrawAmountStr('');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
            showAlert(`❌ ${msg}`, 'Withdrawal Failed');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Skeleton Row Component ──
    const SkeletonRow = ({ index }: { index: number }) => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 0',
            borderBottom: index < 3 ? '1px solid var(--border)' : 'none',
            gap: 12
        }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                <div style={{ height: 12, width: '40%', borderRadius: 6, background: 'linear-gradient(90deg, var(--bg-glass) 25%, rgba(148,163,184,0.12) 50%, var(--bg-glass) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
                <div style={{ height: 10, width: '25%', borderRadius: 6, background: 'linear-gradient(90deg, var(--bg-glass) 25%, rgba(148,163,184,0.12) 50%, var(--bg-glass) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', animationDelay: '0.2s' }} />
            </div>
            <div style={{ height: 12, width: '15%', borderRadius: 6, background: 'linear-gradient(90deg, var(--bg-glass) 25%, rgba(148,163,184,0.12) 50%, var(--bg-glass) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', animationDelay: '0.1s' }} />
            <div style={{ height: 24, width: 60, borderRadius: 12, background: 'linear-gradient(90deg, var(--bg-glass) 25%, rgba(148,163,184,0.12) 50%, var(--bg-glass) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', animationDelay: '0.3s' }} />
        </div>
    );

    // ── Render 4 skeleton rows ──
    const renderSkeletons = () => (
        <>
            {[0, 1, 2, 3].map(i => <SkeletonRow key={i} index={i} />)}
        </>
    );

    // ── Status badge helper ──
    const getStatusBadge = (status: string) => {
        const colors: Record<string, { bg: string; color: string }> = {
            pending: { bg: 'var(--warning-bg)', color: 'var(--warning)' },
            approved: { bg: 'var(--success-bg)', color: 'var(--success)' },
            rejected: { bg: 'var(--danger-bg)', color: 'var(--danger)' },
        };
        const c = colors[status] || colors.pending;
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 12px', borderRadius: 'var(--radius-full)',
                fontSize: '0.73rem', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.3px', background: c.bg, color: c.color,
            }}>
                {status}
            </span>
        );
    };

    // ── Loading state ──
    if (loading || checking) {
        return (
            <div className="page-wrapper" style={{ maxWidth: 720, textAlign: 'center', paddingTop: 100 }}>
                <Loader size={32} className="spin" style={{ color: 'var(--neon-blue)' }} />
                <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Loading wallet…</p>
            </div>
        );
    }

    // ── PIN SETUP GATE ──
    if (hasPinSetup === false) {
        return (
            <div className="page-wrapper" style={{ maxWidth: 720 }}>
                <div className="pin-gate animate-in">
                    <div className="pin-gate-icon setup">
                        <Shield size={36} style={{ color: 'var(--accent-hover)' }} />
                    </div>
                    <h2>Setup Wallet PIN</h2>
                    <p>
                        {setupStep === 'enter'
                            ? 'Create a 6-digit PIN to secure your wallet. You\'ll need this PIN to access your funds or join tournaments.'
                            : 'Re-enter your 6-digit PIN to confirm.'}
                    </p>

                    <PinKeypad
                        onSubmit={async (pin) => {
                            if (setupStep === 'enter') {
                                setFirstPin(pin);
                                setSetupStep('confirm');
                                setPinError('');
                            } else {
                                if (pin !== firstPin) {
                                    setPinError('PINs do not match. Please try again.');
                                    setSetupStep('enter');
                                    setFirstPin('');
                                    throw new Error('mismatch');
                                }
                                try {
                                    await setupPin(pin);
                                    // In mock mode, also mark PIN as set up
                                    try { sessionStorage.setItem('tournahub_mock_pin_setup', 'true'); } catch { /* noop */ }
                                } catch (err: unknown) {
                                    const msg = err instanceof Error ? err.message : 'Failed to set PIN.';
                                    setPinError(msg);
                                    setSetupStep('enter');
                                    setFirstPin('');
                                    throw err;
                                }
                            }
                        }}
                        error={pinError}
                        onClearError={() => setPinError('')}
                        submitLabel={setupStep === 'enter' ? 'Next' : 'Confirm'}
                    />

                    {setupStep === 'confirm' && (
                        <button
                            className="btn btn-secondary"
                            style={{ marginTop: 8, fontSize: '0.85rem' }}
                            onClick={() => { setSetupStep('enter'); setFirstPin(''); setPinError(''); }}
                        >
                            ← Start Over
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // ── PIN UNLOCK GATE ──
    if (!isWalletUnlocked) {
        return (
            <div className="page-wrapper" style={{ maxWidth: 720 }}>
                <div className="pin-gate animate-in">
                    <div className="pin-gate-icon locked">
                        <Lock size={36} style={{ color: 'var(--warning)' }} />
                    </div>
                    <h2>Wallet Locked</h2>
                    <p>Enter your 6-digit PIN to access your wallet.</p>

                    <PinKeypad
                        onSubmit={async (pin) => {
                            try {
                                await verifyPin(pin);
                            } catch (err: unknown) {
                                const msg = err instanceof Error ? err.message : 'Incorrect PIN.';
                                setPinError(msg);
                                throw err;
                            }
                        }}
                        error={pinError}
                        onClearError={() => setPinError('')}
                        submitLabel="Unlock"
                    />
                </div>
            </div>
        );
    }

    // ── History Sub-Tab Buttons ──
    const historySubTabs: { key: typeof activeHistoryTab; label: string; icon: React.ReactNode }[] = [
        { key: 'all', label: 'All', icon: <Wallet size={13} /> },
        { key: 'deposits', label: 'Deposits', icon: <ArrowDownLeft size={13} /> },
        { key: 'withdrawals', label: 'Withdrawals', icon: <ArrowUpRight size={13} /> },
        { key: 'tournaments', label: 'Tournaments', icon: <Trophy size={13} /> },
    ];

    // ── WALLET UNLOCKED — Show full wallet ──
    return (
        <div className="page-wrapper" style={{ maxWidth: 720 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <h1 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.8rem' }} className="animate-in">💰 My Wallet</h1>
                <button
                    className="btn btn-secondary animate-in"
                    style={{ fontSize: '0.78rem', padding: '6px 14px', gap: 5 }}
                    onClick={lockWallet}
                    title="Lock your wallet"
                >
                    <Lock size={14} /> Lock
                </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24 }} className="animate-in">
                <ShieldCheck size={14} style={{ color: 'var(--success)' }} />
                <span style={{ color: 'var(--success)', fontSize: '0.8rem', fontWeight: 500 }}>Wallet Unlocked</span>
            </div>

            {/* Penalty/Frozen Warning Banner */}
            {wallet.walletStatus === 'frozen' ? (
                <div className="animate-in" style={{
                    padding: '16px 20px',
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.25)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 16,
                }}>
                    <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--danger)', marginBottom: 6 }}>
                        ⛔ Your wallet has been frozen due to a policy violation.
                    </div>
                    <p style={{ fontSize: '0.82rem', color: 'var(--danger)', opacity: 0.85, marginBottom: 4, lineHeight: 1.5 }}>
                        You cannot deposit or withdraw until your account is reviewed.
                    </p>
                    <p style={{ fontSize: '0.82rem', color: 'var(--danger)', opacity: 0.7, lineHeight: 1.5 }}>
                        Contact support for assistance.
                    </p>
                </div>
            ) : wallet.penaltyDebt > 0 ? (
                <div className="animate-in" style={{
                    padding: '16px 20px',
                    background: 'rgba(245,158,11,0.08)',
                    border: '1px solid rgba(245,158,11,0.25)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 16,
                }}>
                    <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--warning)' }}>
                        ⚠️ You have a pending penalty of {wallet.penaltyDebt.toLocaleString()} T Coin.
                    </div>
                    <p style={{ fontSize: '0.82rem', color: 'var(--warning)', opacity: 0.85, marginTop: 4, lineHeight: 1.5 }}>
                        This amount will be automatically deducted from your next deposit or withdrawal.
                    </p>
                </div>
            ) : null}

            {/* Balance Card */}
            <div className="wallet-balance-card animate-in">
                <div className="wallet-balance-label">Main Balance</div>
                <div className="wallet-balance-value" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><TCoinIcon size={44} />{wallet.balance.toLocaleString()}</div>
                {wallet.promoBalance > 0 && (
                    <div style={{ textAlign: 'center', marginTop: 4 }}>
                        <span style={{ fontSize: '0.92rem', fontWeight: 600, color: '#f59e0b', fontStyle: 'italic', display: 'inline-flex', alignItems: 'center', gap: 4 }}>+ <TCoinIcon size={14} />{wallet.promoBalance.toLocaleString()} Promo Credits</span>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>Game-restricted • expires</div>
                    </div>
                )}
                <div className="wallet-sub-balances">
                    <div className="wallet-sub">
                        <div className="ws-val" style={{ color: 'var(--warning)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><TCoinIcon size={16} />{wallet.lockedBalance.toLocaleString()}</div>
                        <div className="ws-label">Locked in Tournaments</div>
                    </div>
                    <div className="wallet-sub">
                        <div className="ws-val" style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><TCoinIcon size={16} />{(wallet.balance + wallet.lockedBalance).toLocaleString()}</div>
                        <div className="ws-label">Total Credits</div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
                    <button className="btn btn-primary" onClick={() => setTab('deposit')}>+ Deposit</button>
                    <button className="btn btn-secondary" onClick={() => setTab('withdraw')}>↑ Withdraw</button>
                </div>
            </div>

            {/* ── Promo Code Section ── */}
            <div className="animate-in" style={{ marginTop: -8, marginBottom: 8 }}>
                <button
                    onClick={() => { setShowPromoInput(!showPromoInput); setPromoError(null); setPromoSuccess(null); setPromoPreview(null); }}
                    style={{
                        width: '100%', padding: '12px 18px', borderRadius: 'var(--radius-md)',
                        border: '1px dashed rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.04)',
                        color: '#f59e0b', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        transition: 'all 0.25s',
                    }}
                >
                    <Gift size={16} /> 🎁 Have a Promo Code?
                </button>

                <AnimatePresence>
                    {showPromoInput && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                            style={{ overflow: 'hidden' }}
                        >
                            <div style={{ padding: '16px 0 0' }}>
                                {/* Input Row */}
                                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                    <input
                                        className="form-input"
                                        placeholder="Enter promo code"
                                        maxLength={20}
                                        value={promoCode}
                                        onChange={e => { setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')); setPromoError(null); setPromoSuccess(null); setPromoPreview(null); }}
                                        style={{ fontFamily: "'Roboto Mono', 'Courier New', monospace", letterSpacing: '1.5px', textTransform: 'uppercase', flex: 1 }}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handlePromoCheck(); } }}
                                    />
                                    <button
                                        className="btn btn-primary"
                                        type="button"
                                        disabled={promoChecking || !promoCode.trim()}
                                        onClick={handlePromoCheck}
                                        style={{ minWidth: 80 }}
                                    >
                                        {promoChecking ? <Loader2 size={16} className="spin" /> : 'Check'}
                                    </button>
                                </div>

                                {/* Error Message */}
                                <AnimatePresence>
                                    {promoError && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-md)', color: 'var(--danger)', fontSize: '0.82rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                                <AlertCircle size={15} /> {promoError}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Success Message */}
                                <AnimatePresence>
                                    {promoSuccess && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            <div style={{ padding: '14px 16px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 'var(--radius-md)', color: 'var(--success)', fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'pre-line', marginBottom: 12 }}>
                                                {promoSuccess}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Reveal Card */}
                                <AnimatePresence>
                                    {promoPreview && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            <div style={{ padding: 20, background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 'var(--radius-lg)' }}>
                                                {/* Reward Amount */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                                    <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(251,191,36,0.1))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Gift size={20} style={{ color: '#f59e0b' }} />
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '1.15rem', fontWeight: 800, fontFamily: 'Outfit' }}>🎁 {promoPreview.rewardAmount} T Coin Promo Credits</div>
                                                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Code: <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{promoCode.toUpperCase()}</span></div>
                                                    </div>
                                                </div>

                                                {/* Game Restriction */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                                    <Tag size={14} style={{ color: 'var(--accent-hover, #818cf8)' }} />
                                                    Valid for: <strong>{promoGameLabels[promoPreview.validForGame] || promoPreview.validForGame}</strong>
                                                </div>

                                                {/* Squad Requirement */}
                                                {promoPreview.requireFullSquad && (
                                                    <div style={{ padding: '8px 12px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.12)', borderRadius: 8, marginBottom: 10 }}>
                                                        <div style={{ fontSize: '0.8rem', color: 'var(--neon-blue, #38bdf8)', fontWeight: 600, marginBottom: 2 }}>
                                                            👥 Requires: Full {promoGameLabels[promoPreview.squadGameRequirement || promoPreview.validForGame]} squad — you must be the squad leader
                                                        </div>
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                                            {['pubg', 'freefire'].includes(promoPreview.squadGameRequirement || promoPreview.validForGame)
                                                                ? 'PUBG/Free Fire: 4 members'
                                                                : 'MLBB/Wild Rift: 5 members'}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* FOMO Tracker */}
                                                {(() => {
                                                    const pct = Math.round((promoPreview.currentUses / promoPreview.maxUses) * 100);
                                                    const urgent = pct > 80;
                                                    return (
                                                        <div style={{ marginBottom: 10 }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: 4 }}>
                                                                <span style={{ color: urgent ? 'var(--danger)' : 'var(--text-muted)', fontWeight: urgent ? 700 : 400 }}>
                                                                    {urgent ? '🔥 Almost gone!' : 'Claimed'}
                                                                </span>
                                                                <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{promoPreview.currentUses}/{promoPreview.maxUses}</span>
                                                            </div>
                                                            <div style={{ height: 6, borderRadius: 3, background: 'rgba(148,163,184,0.1)', overflow: 'hidden' }}>
                                                                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: urgent ? 'var(--danger)' : 'var(--success)', transition: 'width 0.4s ease' }} />
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {/* Expiry Note */}
                                                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 5 }}>
                                                    ⏳ Credits expire {promoPreview.lifespanHours}h after redemption
                                                </div>

                                                {/* Redeem Button */}
                                                <button
                                                    className="btn btn-primary"
                                                    style={{ width: '100%', padding: '12px 0', fontSize: '0.92rem', fontWeight: 700 }}
                                                    onClick={handlePromoRedeem}
                                                    disabled={promoRedeeming}
                                                >
                                                    {promoRedeeming ? <><Loader2 size={16} className="spin" /> Redeeming…</> : '🎁 Redeem Now'}
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <div className="tabs animate-in" style={{ margin: '0 auto 24px auto', width: 'fit-content' }}>
                    <button className={`tab-btn ${tab === 'history' ? 'active' : ''}`} onClick={() => { setTab('history'); setFrozenClickMsg(false); }}>History</button>
                    <button
                        className={`tab-btn ${tab === 'deposit' ? 'active' : ''}`}
                        onClick={() => {
                            if (wallet.walletStatus === 'frozen') { setFrozenClickMsg(true); return; }
                            setTab('deposit'); setFrozenClickMsg(false);
                        }}
                        style={wallet.walletStatus === 'frozen' ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    >Deposit</button>
                    <button
                        className={`tab-btn ${tab === 'withdraw' ? 'active' : ''}`}
                        onClick={() => {
                            if (wallet.walletStatus === 'frozen') { setFrozenClickMsg(true); return; }
                            setTab('withdraw'); setFrozenClickMsg(false);
                        }}
                        style={wallet.walletStatus === 'frozen' ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    >Withdraw</button>
                </div>
            </div>
            {frozenClickMsg && wallet.walletStatus === 'frozen' && (
                <div className="animate-in" style={{
                    padding: '12px 16px',
                    background: 'rgba(239,68,68,0.06)',
                    border: '1px solid rgba(239,68,68,0.15)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--danger)',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    marginBottom: 16,
                    marginTop: -16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}>
                    <AlertCircle size={16} /> Your wallet is frozen. Please contact support.
                </div>
            )}

            {/* History Tab */}
            {tab === 'history' && (
                <div className="card animate-in">
                    {/* ── History Sub-Tabs ── */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: 20, padding: 3,
                        background: 'rgba(148,163,184,0.04)', borderRadius: 10,
                        border: '1px solid var(--border)',
                    }}>
                        {historySubTabs.map(st => (
                            <button
                                key={st.key}
                                onClick={() => setActiveHistoryTab(st.key)}
                                style={{
                                    flex: 1, padding: '8px 0', borderRadius: 8,
                                    border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                                    fontSize: '0.78rem', fontWeight: 600,
                                    transition: 'all 0.2s',
                                    background: activeHistoryTab === st.key
                                        ? 'linear-gradient(135deg, rgba(99,102,241,0.9), rgba(168,85,247,0.9))'
                                        : 'transparent',
                                    color: activeHistoryTab === st.key ? '#fff' : 'var(--text-secondary)',
                                }}
                            >
                                {st.icon} {st.label}
                            </button>
                        ))}
                    </div>

                    {/* ── All Tab (uses existing data) ── */}
                    {activeHistoryTab === 'all' && (
                        <>
                            {transactions.length === 0 && (
                                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No transactions yet.</p>
                            )}
                            {transactions.map((tx, i) => (
                                <div key={tx.id} className={`animate-in stagger-${(i % 6) + 1}`} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '14px 0',
                                    borderBottom: i < transactions.length - 1 ? '1px solid var(--border)' : 'none',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--bg-glass)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
                                            {getTypeIcon(tx.type)}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{tx.description}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{tx.createdAt}</div>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, color: tx.amount > 0 ? 'var(--success)' : tx.amount < 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>
                                            {tx.amount > 0 ? '+' : tx.amount < 0 ? '-' : ''}<TCoinIcon size={12} /> {Math.abs(tx.amount).toLocaleString()}
                                        </div>
                                        <span className={`badge ${tx.status}`}>{tx.status}</span>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {/* ── Deposits Tab ── */}
                    {activeHistoryTab === 'deposits' && (
                        <>
                            {depositsLoading ? renderSkeletons() : depositRequests.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No deposit history yet.</p>
                            ) : (
                                depositRequests.map((req, i) => (
                                    <div key={req.id} className={`animate-in stagger-${(i % 6) + 1}`} style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '14px 0',
                                        borderBottom: i < depositRequests.length - 1 ? '1px solid var(--border)' : 'none',
                                        gap: 10,
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                                            <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'rgba(99,102,241,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(99,102,241,0.12)', flexShrink: 0 }}>
                                                <ArrowDownLeft size={16} style={{ color: '#818cf8' }} />
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                {/* Deposit ID Badge */}
                                                {req.depositId && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                        <span style={{
                                                            fontFamily: "'Roboto Mono', 'Courier New', monospace",
                                                            fontSize: '0.72rem', fontWeight: 600,
                                                            color: '#818cf8',
                                                            background: 'rgba(99,102,241,0.1)',
                                                            padding: '2px 8px', borderRadius: 6,
                                                        }}>
                                                            {req.depositId}
                                                        </span>
                                                        <button
                                                            onClick={() => handleCopyId(req.depositId)}
                                                            style={{
                                                                background: 'none', border: 'none', cursor: 'pointer',
                                                                color: copiedId === req.depositId ? 'var(--success)' : 'var(--text-muted)',
                                                                padding: 2, display: 'flex', alignItems: 'center',
                                                                transition: 'color 0.2s',
                                                            }}
                                                            title="Copy ID"
                                                        >
                                                            {copiedId === req.depositId ? <Check size={13} /> : <Copy size={13} />}
                                                        </button>
                                                    </div>
                                                )}
                                                <div style={{ fontWeight: 600, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 4 }}><TCoinIcon size={12} /> {Math.abs(req.amount ?? 0).toLocaleString()}</div>
                                                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    {req.method && <span style={{ textTransform: 'capitalize' }}>{req.method}</span>}
                                                    {req.method && req.createdAt && <span>·</span>}
                                                    <span>{req.createdAt}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ flexShrink: 0 }}>
                                            {getStatusBadge(req.status)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </>
                    )}

                    {/* ── Withdrawals Tab ── */}
                    {activeHistoryTab === 'withdrawals' && (
                        <>
                            {withdrawalsLoading ? renderSkeletons() : withdrawalRequests.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No withdrawal history yet.</p>
                            ) : (
                                withdrawalRequests.map((req, i) => (
                                    <div key={req.id} className={`animate-in stagger-${(i % 6) + 1}`} style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '14px 0',
                                        borderBottom: i < withdrawalRequests.length - 1 ? '1px solid var(--border)' : 'none',
                                        gap: 10,
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                                            <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'rgba(16,185,129,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(16,185,129,0.12)', flexShrink: 0 }}>
                                                <ArrowUpRight size={16} style={{ color: '#34d399' }} />
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                {/* Withdrawal ID Badge */}
                                                {req.withdrawalId && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                        <span style={{
                                                            fontFamily: "'Roboto Mono', 'Courier New', monospace",
                                                            fontSize: '0.72rem', fontWeight: 600,
                                                            color: '#34d399',
                                                            background: 'rgba(16,185,129,0.1)',
                                                            padding: '2px 8px', borderRadius: 6,
                                                        }}>
                                                            {req.withdrawalId}
                                                        </span>
                                                        <button
                                                            onClick={() => handleCopyId(req.withdrawalId)}
                                                            style={{
                                                                background: 'none', border: 'none', cursor: 'pointer',
                                                                color: copiedId === req.withdrawalId ? 'var(--success)' : 'var(--text-muted)',
                                                                padding: 2, display: 'flex', alignItems: 'center',
                                                                transition: 'color 0.2s',
                                                            }}
                                                            title="Copy ID"
                                                        >
                                                            {copiedId === req.withdrawalId ? <Check size={13} /> : <Copy size={13} />}
                                                        </button>
                                                    </div>
                                                )}
                                                <div style={{ fontWeight: 600, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 4 }}><TCoinIcon size={12} /> {Math.abs(req.amount ?? 0).toLocaleString()}</div>
                                                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    {req.method && <span style={{ textTransform: 'capitalize' }}>{req.method}</span>}
                                                    {req.method && req.createdAt && <span>·</span>}
                                                    <span>{req.createdAt}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ flexShrink: 0 }}>
                                            {getStatusBadge(req.status)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </>
                    )}

                    {/* ── Tournaments Tab ── */}
                    {activeHistoryTab === 'tournaments' && (
                        <>
                            {tournamentsLoading ? renderSkeletons() : tournamentTxns.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No tournament activity yet.</p>
                            ) : (
                                tournamentTxns.map((tx, i) => (
                                    <div key={tx.id} className={`animate-in stagger-${(i % 6) + 1}`} style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '14px 0',
                                        borderBottom: i < tournamentTxns.length - 1 ? '1px solid var(--border)' : 'none',
                                        gap: 10,
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                width: 36, height: 36, borderRadius: 'var(--radius-md)',
                                                background: tx.type === 'entry' ? 'rgba(245,158,11,0.06)' : 'rgba(56,189,248,0.06)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                border: `1px solid ${tx.type === 'entry' ? 'rgba(245,158,11,0.12)' : 'rgba(56,189,248,0.12)'}`,
                                                flexShrink: 0,
                                            }}>
                                                {tx.type === 'entry'
                                                    ? <Send size={16} style={{ color: 'var(--warning)' }} />
                                                    : <Trophy size={16} style={{ color: 'var(--neon-blue)' }} />
                                                }
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {tx.description || (tx.type === 'entry' ? 'Tournament Entry' : 'Tournament Prize')}
                                                </div>
                                                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{tx.createdAt}</div>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                                            <div style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4,
                                                fontWeight: 700, fontSize: '0.9rem',
                                                color: tx.type === 'reward' ? 'var(--success)' : 'var(--danger)',
                                            }}>
                                                {tx.type === 'reward' ? '+' : '-'}<TCoinIcon size={12} /> {Math.abs(tx.amount ?? 0).toLocaleString()}
                                            </div>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center',
                                                padding: '3px 10px', borderRadius: 'var(--radius-full)',
                                                fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                                background: tx.type === 'entry' ? 'rgba(245,158,11,0.1)' : 'rgba(56,189,248,0.1)',
                                                color: tx.type === 'entry' ? 'var(--warning)' : 'var(--neon-blue)',
                                            }}>
                                                {tx.type === 'entry' ? 'Entry Fee' : 'Prize'}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Deposit Tab */}
            {tab === 'deposit' && (
                <div className="card animate-in">
                    <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, marginBottom: 20 }}>Deposit T Coins</h3>
                    {wallet.penaltyDebt > 0 && (
                        <div style={{
                            padding: '14px 18px',
                            background: 'rgba(59,130,246,0.06)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid rgba(59,130,246,0.12)',
                            marginBottom: 18,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 10,
                        }}>
                            <span style={{ flexShrink: 0, fontSize: '1rem' }}>ℹ️</span>
                            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                                <strong>Note:</strong> {wallet.penaltyDebt.toLocaleString()} T Coin of this deposit will be automatically applied to your outstanding penalty debt.
                            </p>
                        </div>
                    )}
                    <form onSubmit={handleDeposit}>
                        <div className="form-group">
                            <label className="form-label">Payment Method</label>
                            <select className="form-input" value={depositMethod} onChange={e => { setDepositMethod(e.target.value); setCopied(false); }}>
                                <option value="" disabled>Select a payment method</option>
                                {methods.map(m => <option key={m}>{m}</option>)}
                            </select>
                        </div>

                        {/* ── Payment Number Slide-Down Box ── */}
                        <AnimatePresence mode="wait">
                            {depositMethod !== '' && (
                            <motion.div
                                key={depositMethod}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                                style={{ overflow: 'hidden', marginBottom: 8 }}
                            >
                                {paymentConfigLoading ? (
                                    /* Loading Skeleton */
                                    <div style={{ padding: '16px 20px', background: 'rgba(99,102,241,0.04)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                                        <div style={{ height: 14, width: '40%', background: 'rgba(148,163,184,0.12)', borderRadius: 6, marginBottom: 10, animation: 'pulse 1.5s ease-in-out infinite' }} />
                                        <div style={{ height: 28, width: '65%', background: 'rgba(148,163,184,0.10)', borderRadius: 6, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                                        <div style={{ height: 12, width: '50%', background: 'rgba(148,163,184,0.08)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
                                    </div>
                                ) : activeNumbers.length > 0 ? (
                                    /* Has active numbers */
                                    <div style={{ padding: '16px 20px', background: 'rgba(99,102,241,0.04)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(99,102,241,0.12)' }}>
                                        {/* Sub-tabs — only show when multiple types */}
                                        {hasMultipleTypes && (
                                            <div style={{
                                                display: 'flex', gap: 4, marginBottom: 14, padding: 3,
                                                background: 'rgba(148,163,184,0.06)', borderRadius: 10,
                                                border: '1px solid var(--border)',
                                            }}>
                                                {numberTypes.map(t => {
                                                    const info = typeLabels[t];
                                                    const isActive = activeNumberType === t;
                                                    return (
                                                        <button
                                                            key={t}
                                                            type="button"
                                                            onClick={() => { setActiveNumberType(t); setCopied(false); }}
                                                            style={{
                                                                flex: 1, padding: '7px 10px', borderRadius: 8,
                                                                border: 'none', cursor: 'pointer',
                                                                fontSize: '0.76rem', fontWeight: 600,
                                                                transition: 'all 0.2s',
                                                                background: isActive
                                                                    ? `linear-gradient(135deg, ${info.bg}, ${info.bg})`
                                                                    : 'transparent',
                                                                color: isActive ? info.color : 'var(--text-muted)',
                                                                boxShadow: isActive ? `0 0 0 1px ${info.border}` : 'none',
                                                            }}
                                                        >
                                                            {info.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Number display */}
                                        {displayedNumber ? (
                                            <>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8, fontWeight: 500 }}>Send to this {depositMethod} number:</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                                    <span style={{ fontFamily: "'Roboto Mono', 'Courier New', monospace", fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '1.5px' }}>
                                                        {displayedNumber.number}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCopyNumber(displayedNumber.number)}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? 'var(--success)' : 'var(--text-muted)', padding: 4, transition: 'color 0.2s', display: 'flex', alignItems: 'center' }}
                                                        title="Copy number"
                                                    >
                                                        {copied ? <Check size={18} /> : <Copy size={18} />}
                                                    </button>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '3px 10px',
                                                        borderRadius: 999,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.5,
                                                        background: typeLabels[displayedNumber.type]?.bg ?? 'rgba(59,130,246,0.12)',
                                                        color: typeLabels[displayedNumber.type]?.color ?? '#3b82f6',
                                                        border: `1px solid ${typeLabels[displayedNumber.type]?.border ?? 'rgba(59,130,246,0.25)'}`,
                                                    }}>
                                                        {typeLabels[displayedNumber.type]?.label ?? 'Personal'}
                                                    </span>
                                                </div>
                                                <p style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 8 }}>
                                                    <span>Open {depositMethod} → choose{' '}
                                                        <span style={{
                                                            color: typeLabels[displayedNumber.type]?.color ?? '#a78bfa',
                                                            fontWeight: 700,
                                                            background: typeLabels[displayedNumber.type]?.bg ?? 'rgba(167,139,250,0.1)',
                                                            padding: '1px 6px',
                                                            borderRadius: 4,
                                                        }}>
                                                            {typeLabels[displayedNumber.type]?.instruction ?? 'Send Money'}
                                                        </span>
                                                        {' '}→ Enter the number above.
                                                    </span>
                                                </p>
                                            </>
                                        ) : (
                                            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>Select a payment type above.</p>
                                        )}
                                    </div>
                                ) : currentMethodInfo && currentMethodInfo.numbers && currentMethodInfo.numbers.length > 0 ? (
                                    /* All numbers inactive */
                                    <div style={{ padding: '14px 18px', background: 'rgba(245,158,11,0.06)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <AlertCircle size={18} style={{ color: '#ca8a04', flexShrink: 0 }} />
                                        <p style={{ fontSize: '0.82rem', color: '#ca8a04', margin: 0 }}>
                                            {depositMethod} is temporarily unavailable. Please choose another method.
                                        </p>
                                    </div>
                                ) : (
                                    /* Not Configured */
                                    <div style={{ padding: '14px 18px', background: 'rgba(245,158,11,0.06)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <AlertCircle size={18} style={{ color: '#ca8a04', flexShrink: 0 }} />
                                        <p style={{ fontSize: '0.82rem', color: '#ca8a04', margin: 0 }}>
                                            Currently unavailable. Please try another method.
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="form-group">
                            <label className="form-label">Amount (T Coin)</label>
                            <input className="form-input" name="amount" type="number" placeholder="e.g. 500" min={100} max={50000} required />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Transaction ID{depositMethod ? ` (from ${depositMethod})` : ''}</label>
                            <input className="form-input" name="transactionId" placeholder="Enter your txn ID" required maxLength={30} pattern="^[a-zA-Z0-9]{4,30}$" title="4-30 alphanumeric characters" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Sender Number</label>
                            <input className="form-input" name="senderNumber" placeholder="01XXXXXXXXX" required maxLength={16} pattern="^(\+?880)?01[3-9]\d{8}$" title="BD phone: 01XXXXXXXXX or +88001XXXXXXXXX" />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginBottom: 16 }}>
                            <div style={{ width: 'fit-content', background: 'rgba(234, 179, 8, 0.12)', border: '1px solid rgba(234, 179, 8, 0.3)', color: '#ca8a04', borderRadius: 999, padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600 }}>
                                Minimum deposit: 100 T Coin. Deposits are typically reviewed and credited within 30–45 minutes.
                            </div>
                        </div>
                        <button className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={submitting}>
                            {submitting ? 'Submitting…' : 'Submit Deposit Request'}
                        </button>
                    </form>
                </div>
            )}

            {/* Withdraw Tab */}
            {tab === 'withdraw' && (
                <div className="card animate-in">
                    <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, marginBottom: 20 }}>Withdraw T Coins</h3>
                    <form onSubmit={handleWithdraw}>
                        <div className="form-group">
                            <label className="form-label">Withdrawal Method</label>
                            <select className="form-input" name="method" value={withdrawMethod} onChange={(e) => setWithdrawMethod(e.target.value)}>
                                {methods.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Amount (T Coin)</label>
                            <input className="form-input" name="amount" type="number" placeholder="e.g. 1000" min={200} max={wallet.balance} required onChange={e => setWithdrawAmountStr(e.target.value)} />
                        </div>
                        {/* Penalty Calculation Block */}
                        {wallet.penaltyDebt > 0 && (() => {
                            const amt = Number(withdrawAmountStr) || 0;
                            if (amt <= 0) return null;
                            const debt = wallet.penaltyDebt;
                            const isFullInterception = amt <= debt;
                            return (
                                <div style={{
                                    padding: '16px 20px',
                                    background: isFullInterception ? 'rgba(245,158,11,0.06)' : 'rgba(99,102,241,0.04)',
                                    border: `1px solid ${isFullInterception ? 'rgba(245,158,11,0.2)' : 'rgba(99,102,241,0.12)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: 16,
                                }}>
                                    {isFullInterception ? (
                                        <>
                                            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--warning)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                ⚠️ This withdrawal will be fully applied to your penalty debt.
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', padding: '6px 0', color: 'var(--text-secondary)' }}>
                                                <span>You will receive:</span>
                                                <span style={{ fontWeight: 700, color: 'var(--danger)' }}>0 T Coin</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', padding: '6px 0', color: 'var(--text-secondary)' }}>
                                                <span>Remaining debt:</span>
                                                <span style={{ fontWeight: 700, color: 'var(--warning)' }}>{(debt - amt).toLocaleString()} T Coin</span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', padding: '6px 0', color: 'var(--text-secondary)' }}>
                                                <span>Withdrawal amount:</span>
                                                <span style={{ fontWeight: 600 }}>{amt.toLocaleString()} T Coin</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', padding: '6px 0', color: 'var(--text-secondary)' }}>
                                                <span>Penalty deduction:</span>
                                                <span style={{ fontWeight: 700, color: 'var(--danger)' }}>−{debt.toLocaleString()} T Coin</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', padding: '6px 0', borderTop: '1px solid var(--border)', marginTop: 4 }}>
                                                <span style={{ fontWeight: 600 }}>You will receive:</span>
                                                <span style={{ fontWeight: 800, color: 'var(--success)' }}>{(amt - debt).toLocaleString()} T Coin</span>
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: 4, textAlign: 'right' }}>
                                                Remaining debt: 0 T Coin (cleared!)
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })()}
                        <div className="form-group">
                            <label className="form-label">Receiver Number</label>
                            <input className="form-input" name="receiverNumber" placeholder={`Type your ${withdrawMethod} number`} required maxLength={16} pattern="^(\+?880)?01[3-9]\d{8}$" title="BD phone: 01XXXXXXXXX or +88001XXXXXXXXX" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Confirm Receiver Number</label>
                            <input className="form-input" name="confirmReceiverNumber" placeholder={`Retype your ${withdrawMethod} number`} required maxLength={16} pattern="^(\+?880)?01[3-9]\d{8}$" title="Must precisely match the number above" />
                        </div>
                        <div style={{ padding: 16, background: 'rgba(99,102,241,0.06)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(99,102,241,0.12)', marginBottom: 12 }}>
                            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                ℹ️ Your withdrawal request will be securely processed within 24 hours. Please note that the minimum withdrawal amount is 200 T Coin.
                            </p>
                        </div>
                        {/* Withdrawal Status Banner */}
                        <div style={{ padding: 16, background: withdrawalLimitReached ? 'rgba(239,68,68,0.06)' : 'var(--warning-bg)', borderRadius: 'var(--radius-md)', border: `1px solid ${withdrawalLimitReached ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)'}`, marginBottom: 16 }}>
                            <p style={{ fontSize: '0.82rem', color: withdrawalLimitReached ? 'var(--danger)' : 'var(--warning)', marginBottom: 4, fontWeight: 600 }}>
                                {withdrawalLimitReached ? '🚫 Daily Withdrawal Limit Reached' : '⚠️ Daily Withdrawal Limit'}
                            </p>
                            <p style={{ fontSize: '0.8rem', color: withdrawalLimitReached ? 'var(--danger)' : 'var(--warning)', opacity: 0.85 }}>
                                {withdrawalLimitReached
                                    ? 'You have used both withdrawal requests for today. Please try again after 24 hours.'
                                    : `You may submit up to 2 withdrawal requests per 24-hour window. Remaining today: ${remainingWithdrawals}`
                                }
                            </p>
                        </div>
                        <button className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={submitting || withdrawalLimitReached}>
                            {withdrawalLimitReached ? 'Daily Limit Reached' : submitting ? 'Submitting…' : 'Submit Withdrawal Request'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
}
